import React from 'react';
import { useNavigate } from 'react-router-dom';

import Empty from 'antd/lib/empty';

import { MenuItem } from '@common/react/objects/MenuItem';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { Icon } from '@app/components/Routes/DashboardMenu';
import { isTextInclude } from '@app/components/Utils';
import SearchModalItem from '@app/components/UI/SearchModal/SearchModalItem';
import { SearchObject, SearchObjectType, SearchObjectTypePathName } from '@app/components/UI/SearchModal/index';
import { User, UserRole } from '@app/objects/User';

interface MenuItemWithIcon extends MenuItem {
	icon?: Array<string | Icon>;
	isFavorite?: boolean;
	id?: number;
	type?: string;
	group?: boolean;
	scrollIntoView?: boolean;
}

interface Props {
	search: string;
	items: Array<MenuItemWithIcon>;
	loading: boolean;
	searchItems: Array<SearchObject>;
	open: boolean;
}

const allowTransmutationRoles = [UserRole.Admin];

const SearchMenu: React.FC<Props> = (props) => {
	const { onTransmutation, request, getUser } = useApplicationContext();
	const {
		items,
		search,
		searchItems,
		loading,
		open,
	} = props;
	const navigate = useNavigate();
	const user = getUser<User>();
	const allowTransmutation = React.useMemo(() => {
		return user?.root || allowTransmutationRoles.some((item) => item === user?.role) || user?.transmutation;
	}, [user, user?.transmutation]);

	const [userLoadingId, setUserLoadingId] = React.useState(0);

	const menuItems = React.useMemo(() => {
		const res: Array<MenuItemWithIcon | SearchObject> = searchItems;
		const leftMenu = search ? (items || []).reduce((acc: Array<MenuItemWithIcon>, value: MenuItemWithIcon) => {
			let current = value;
			const includeText = isTextInclude(search, value.name);
			if (value.name === 'External Services') {
				return acc;
			}

			if (value.children && !includeText) {
				current = JSON.parse(JSON.stringify(value));
				current.children!.list = current.children!.list.filter((el) => isTextInclude(search, el.name));
			}

			return includeText || current.children?.list.length ? [...acc, value] : [...acc];
		}, [])
			.reduce((acc: Array<MenuItemWithIcon>, value: MenuItemWithIcon) => {
				if (value.children) {
					return [
						...acc,
						{
							...value,
							group: true,
							children: undefined,
						},
						...value.children!.list.filter((item) => isTextInclude(search, item.name))];
				}

				return [...acc, value];
			}, []) : [];
		return res.concat(leftMenu);
	}, [items, search, searchItems, loading]);

	const [index, setIndex] = React.useState<number>(() => (menuItems[0]?.group ? 1 : 0));

	const onInputKeyDown = (e, items: any = menuItems) => {
		if (e.code === 'ArrowDown') {
			e.preventDefault();
			setIndex((prev) => {
				const nextIndex = items?.[prev + 1]?.group ? prev + 2 : prev + 1;
				if (nextIndex >= items.length) {
					const index = items?.[0]?.group ? 1 : 0;
					if (items[index]) {
						items[index].scrollIntoView = true;
					}
					return index;
				}
				if (items[index]) {
					items[nextIndex].scrollIntoView = true;
				}
				return nextIndex;
			});
		}

		if (e.code === 'ArrowUp') {
			e.preventDefault();
			setIndex((prev) => {
				const nextIndex = items?.[prev - 1]?.group ? prev - 2 : prev - 1;
				if (nextIndex <= -1) {
					if (items[items.length - 1]) {
						items[items.length - 1].scrollIntoView = true;
					}
					return items.length - 1;
				}
				if (items[nextIndex]) {
					items[nextIndex].scrollIntoView = true;
				}
				return nextIndex;
			});
		}

		if (e.code === 'Enter') {
			setIndex((prev) => {
				const item = items[prev];
				if (item) {
					const state = { prevPath: `${location.pathname}${location.search}${location.hash}` };
					if (item.id) {
						navigate(`/${SearchObjectTypePathName[item.type]}-editor/${item.id}`, { state });
					} else {
						if (item.externalLink) {
							const a = document.createElement('a');
							a.target = '_blank';
							a.href = item.path;
							a.click();
						}
						navigate(
							`${item.basePath || ''}${item.path}`,
							{ state },
						);
					}
				}

				return prev;
			});
		}

		if (e.code === 'KeyB' && (e.ctrlKey || e.metaKey) && open) {
			e.preventDefault();
			setIndex((prev) => {
				if (items[prev] && items[prev].type === SearchObjectType.User) {
					const id = items[prev].id;

					const onSuccess = onTransmutation || window.location.reload.bind(window.location);
					const onError = window.location.reload.bind(window.location);
					setUserLoadingId(id);

					request('transmutation', {
						id,
						transmutation: true,
					}).then(() => {
						onSuccess();
					}).catch((error) => {
						console.error(`Transmutation error: ${error}`);
						onError();
					})
						.finally(() => {
							setUserLoadingId(-1);
						});
				}

				return prev;
			});
		}
	};

	React.useEffect(() => {
		setIndex(menuItems[0]?.group ? 1 : 0);
	}, [searchItems, search]);

	React.useEffect(() => {
		if (open) {
			document.addEventListener('keydown', onInputKeyDown);
		}
		return () => {
			open && document.removeEventListener('keydown', onInputKeyDown);
		};
	}, [menuItems, open]);
	const currentItem = menuItems?.[index];

	const onSelect = (item) => {
		const index = item.id
			? menuItems.findIndex((el) => el.id === item.id && el.type === item.type)
			: menuItems.findIndex((el) => el.name === item.name && el.path === item.path);
		setIndex(index);
	};

	return <>
		<ul className="search-modal-list">
			{menuItems.map((item) => <SearchModalItem
				search={search}
				userLoadingId={userLoadingId}
				allowTransmutation={allowTransmutation}
				isSelected={currentItem?.id === undefined
					? item.name === currentItem?.name && currentItem?.path === item.path
					: currentItem?.id === item.id && currentItem?.type === item.type}
				onSelect={onSelect}
				item={item}
				key={item.id === undefined ? `${item.path}${item.name}` : `${item.id} ${item.type}`}
			/>)}
		</ul>
		{!menuItems.length && !loading
			? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={search ? `No results for "${search}"` : 'No recent searches'}
				/>
			)
			: null}
	</>;
};

export default SearchMenu;
